/**
 * AI Data Store
 * Store para dados extraídos via OCR por documento
 */

import { create } from 'zustand';
import type { ExtractResult } from '@/lib/ocr/extractFromFiles';
import { supabase } from '@/integrations/supabase/client';

export type FieldType = 'name' | 'cpf' | 'rg' | 'address' | 'phone' | 'email' | 'date' | 'unknown';

export interface AIDocData {
  docId: string;
  extracts: ExtractResult[];
  timestamp: number;
  lastUpdatedAt: number;
}

export interface ImportantData {
  names: string[];
  documents: string[];
  contacts: string[];
  addresses: string[];
  dates: string[];
}

interface AIDocDataState {
  extractsByDoc: Record<string, {
    rawText?: string;
    structured?: any;
    version: number; // muda a cada update p/ disparar re-render
  }>;
  
  // Universal selectors
  saveOCR: (docId: string, payload: { rawText?: string; structured?: any }, userId?: string) => Promise<void>;
  loadOCR: (docId: string, userId?: string) => Promise<void>;
  hasData: (docId: string) => boolean;
  getRawText: (docId: string) => string | undefined;
  getStructuredData: (docId: string) => any | undefined;
  getDisplayData: (docId: string) => Array<{ label: string; value: string }>;

  // Legacy methods (mantém compatibilidade)
  dataByDoc: Record<string, AIDocData>;
  addExtracts: (docId: string, extracts: ExtractResult[]) => void;
  getAllText: (docId: string) => string;
  getCandidates: (docId: string, fieldType: FieldType) => string[];
  getAllImportantData: (docId: string) => ImportantData;
  parseStructuredFields: (text: string) => string[];
  clearDocData: (docId: string) => void;
  hasStructuredData: (docId: string) => boolean;
  generateDisplayDataFromRaw: (docId: string) => any | null;
}

/**
 * Inferir tipo de campo baseado no texto selecionado (com detecção inteligente)
 */
export function inferFieldType(selectedText: string): FieldType {
  const text = selectedText.toLowerCase().trim();
  
  // Detecção por palavras-chave (mantém compatibilidade)
  if (text.includes('nome') || text.includes('[nome]') || text.includes('[name]')) {
    return 'name';
  }
  
  if (text.includes('cpf')) {
    return 'cpf';
  }
  
  if (text.includes('rg')) {
    return 'rg';
  }
  
  if (text.includes('endereço') || text.includes('endereco') || text.includes('address')) {
    return 'address';
  }
  
  if (text.includes('telefone') || text.includes('phone') || text.includes('cel')) {
    return 'phone';
  }
  
  if (text.includes('email') || text.includes('e-mail')) {
    return 'email';
  }
  
  if (text.includes('data') || text.includes('date')) {
    return 'date';
  }
  
  // Detecção automática por padrão do conteúdo (nova inteligência)
  
  // CPF: 000.000.000-00 ou 00000000000
  if (/^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/.test(text.replace(/\s/g, ''))) {
    return 'cpf';
  }
  
  // RG: 00.000.000-0 ou variações
  if (/^\d{1,2}\.?\d{3}\.?\d{3}-?[0-9Xx]$/.test(text.replace(/\s/g, ''))) {
    return 'rg';
  }
  
  // Data: dd/mm/aaaa
  if (/^(0?[1-9]|[12]\d|3[01])[\/\-](0?[1-9]|1[0-2])[\/\-]\d{4}$/.test(text.replace(/\s/g, ''))) {
    return 'date';
  }
  
  // Email
  if (/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/.test(text)) {
    return 'email';
  }
  
  // Telefone: (00) 0000-0000 ou (00) 00000-0000
  if (/^(\(?\d{2}\)?\s?)?\d{4,5}-?\d{4}$/.test(text.replace(/\s/g, ''))) {
    return 'phone';
  }
  
  // Nome: 2+ palavras capitalizadas, sem números no início
  if (text.length >= 2 && 
      /^[A-ZÁÉÍÓÚÂÊÎÔÛÀÈÌÒÙÇÑ][a-záéíóúâêîôûàèìòùçñ]+(?:\s+[A-ZÁÉÍÓÚÂÊÎÔÛÀÈÌÒÙÇÑ][a-záéíóúâêîôûàèìòùçñ]+)+$/.test(selectedText.trim()) &&
      text.split(' ').length >= 2 && text.split(' ').length <= 6) {
    return 'name';
  }
  
  return 'unknown';
}

/**
 * Separar texto em campos estruturados menores
 */
function parseStructuredFields(text: string): string[] {
  const fields: string[] = [];
  
  // Quebrar por linhas e filtrar campos úteis
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  
  for (const line of lines) {
    // Se linha muito longa, tentar quebrar por padrões
    if (line.length > 80) {
      // Quebrar por vírgulas, pontos-e-vírgulas
      const subFields = line.split(/[,;]/).map(f => f.trim()).filter(f => f.length > 2);
      fields.push(...subFields);
    } else if (line.length >= 2) {
      fields.push(line);
    }
  }
  
  // Remover duplicatas e limitar
  return [...new Set(fields)].slice(0, 20);
}

/**
 * Extrair candidatos de um tipo específico do texto completo
 */
function extractCandidates(fullText: string, fieldType: FieldType): string[] {
  const candidates: string[] = [];
  
  switch (fieldType) {
    case 'cpf':
      // Regex para CPF: 000.000.000-00 ou 00000000000
      const cpfRegex = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g;
      const cpfMatches = fullText.match(cpfRegex) || [];
      candidates.push(...cpfMatches.filter(validateCPF));
      break;
      
    case 'rg':
      // Regex para RG: 00.000.000-0 ou variações
      const rgRegex = /\b\d{1,2}\.?\d{3}\.?\d{3}-?[0-9Xx]\b/g;
      const rgMatches = fullText.match(rgRegex) || [];
      candidates.push(...rgMatches);
      break;
      
    case 'date':
      // Regex para datas: dd/mm/aaaa ou dd-mm-aaaa
      const dateRegex = /\b(0?[1-9]|[12]\d|3[01])[\/\-](0?[1-9]|1[0-2])[\/\-]\d{4}\b/g;
      const dateMatches = fullText.match(dateRegex) || [];
      candidates.push(...dateMatches);
      break;
      
    case 'name':
      // Regex para nomes: 2-6 palavras capitalizadas
      const nameRegex = /\b[A-ZÁÉÍÓÚÂÊÎÔÛÀÈÌÒÙÇÑ][a-záéíóúâêîôûàèìòùçñ]+(?:\s+[A-ZÁÉÍÓÚÂÊÎÔÛÀÈÌÒÙÇÑ][a-záéíóúâêîôûàèìòùçñ]+){1,5}\b/g;
      const nameMatches = fullText.match(nameRegex) || [];
      candidates.push(...nameMatches.filter(name => 
        name.length >= 5 && 
        name.length <= 60 && 
        !name.match(/^\d/) &&
        name.split(' ').length >= 2
      ));
      break;
      
    case 'phone':
      // Regex para telefones: (00) 0000-0000 ou (00) 00000-0000
      const phoneRegex = /\(?\d{2}\)?\s?\d{4,5}-?\d{4}/g;
      const phoneMatches = fullText.match(phoneRegex) || [];
      candidates.push(...phoneMatches);
      break;
      
    case 'email':
      // Regex para emails
      const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
      const emailMatches = fullText.match(emailRegex) || [];
      candidates.push(...emailMatches);
      break;
      
    case 'address':
      // Heurística para endereços: linhas com palavras como "rua", "av", "número"
      const lines = fullText.split('\n');
      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.length > 10 && 
            (trimmed.toLowerCase().includes('rua ') || 
             trimmed.toLowerCase().includes('av ') ||
             trimmed.toLowerCase().includes('avenida ') ||
             trimmed.match(/\d+/))) {
          candidates.push(trimmed);
        }
      }
      break;
  }
  
  // Remover duplicatas e limitar a 10 candidatos
  return [...new Set(candidates)].slice(0, 10);
}

/**
 * Validar CPF usando algoritmo dos dígitos verificadores
 */
function validateCPF(cpf: string): boolean {
  // Remove formatação
  const numbers = cpf.replace(/\D/g, '');
  
  if (numbers.length !== 11) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{10}$/.test(numbers)) return false;
  
  // Calcula os dígitos verificadores
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(numbers[i]) * (10 - i);
  }
  let digit1 = 11 - (sum % 11);
  if (digit1 > 9) digit1 = 0;
  
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(numbers[i]) * (11 - i);
  }
  let digit2 = 11 - (sum % 11);
  if (digit2 > 9) digit2 = 0;
  
  return parseInt(numbers[9]) === digit1 && parseInt(numbers[10]) === digit2;
}

/**
 * Store principal
 */
export const useAIDocDataStore = create<AIDocDataState>((set, get) => ({
  extractsByDoc: {},
  dataByDoc: {},

  // Universal methods
  saveOCR: async (docId, payload, userId) => {
    // Salvar em memória
    set(s => {
      const prev = s.extractsByDoc[docId] ?? { version: 0 };
      return {
        extractsByDoc: {
          ...s.extractsByDoc,
          [docId]: {
            rawText: payload.rawText ?? prev.rawText,
            structured: payload.structured ?? prev.structured,
            version: (prev.version ?? 0) + 1
          }
        }
      };
    });
    
    // Persistir no DB se userId fornecido
    if (userId) {
      try {
        await supabase.from('ocr_extractions').upsert({
          user_id: userId,
          document_id: docId,
          raw_text: payload.rawText || get().extractsByDoc[docId]?.rawText || '',
          structured_data: payload.structured || get().extractsByDoc[docId]?.structured || {},
        }, {
          onConflict: 'user_id,document_id'
        });
        console.log('✅ Dados OCR persistidos no DB');
      } catch (error) {
        console.error('❌ Erro ao persistir OCR:', error);
      }
    }
  },

  loadOCR: async (docId, userId) => {
    if (!userId) return;
    
    try {
      const { data, error } = await supabase
        .from('ocr_extractions')
        .select('*')
        .eq('user_id', userId)
        .eq('document_id', docId)
        .maybeSingle();
      
      if (error) throw error;
      
      if (data) {
        set(s => ({
          extractsByDoc: {
            ...s.extractsByDoc,
            [docId]: {
              rawText: data.raw_text || undefined,
              structured: data.structured_data || undefined,
              version: (s.extractsByDoc[docId]?.version ?? 0) + 1
            }
          }
        }));
        console.log('✅ Dados OCR carregados do DB');
      }
    } catch (error) {
      console.error('❌ Erro ao carregar OCR:', error);
    }
  },

  hasData: (docId) => {
    const e = get().extractsByDoc[docId];
    if (!e) return false;
    return !!(e.structured || e.rawText);
  },

  getRawText: (docId) => get().extractsByDoc[docId]?.rawText,

  getStructuredData: (docId) => get().extractsByDoc[docId]?.structured,

  getDisplayData: (docId) => {
    const s = get().extractsByDoc[docId]?.structured;
    const out: Array<{label:string; value:string}> = [];
    const push = (label:string, v:any) => { if (v) out.push({ label, value: String(v) }); };

    if (s?.document) push('Documento', `${s.document.type} - ${s.document.name ?? ''}`.trim());
    if (s?.identification) {
      push('Nome', s.identification.fullName);
      push('CPF', s.identification.cpf);
      push('RG', s.identification.rg);
      push('CNH', s.identification.cnh);
    }
    if (s?.issuer) { push('Órgão', s.issuer.org); push('UF', s.issuer.uf); }
    if (s?.parents) { push('Filiação (Pai)', s.parents.father); push('Filiação (Mãe)', s.parents.mother); }
    if (s?.dates) { push('Nascimento', s.dates.birth); push('Emissão', s.dates.issue); push('Validade', s.dates.expiry); }
    if (s?.registry) { push('Nº Registro', s.registry.numeroRegistro); push('1ª Habilitação', s.registry.primeiraHabilitacao); }
    if (s?.license) { push('Permissão', s.license.permission); push('ACC', s.license.acc); push('Categoria', s.license.category); }
    if (s?.customer?.name) push('Titular', s.customer.name);
    if (s?.customer?.cpfCnpj) push('CPF/CNPJ', s.customer.cpfCnpj);
    if (s?.billing?.valorTotal) push('Total a pagar', s.billing.valorTotal);
    if (s?.billing?.codigoBarrasOuLinha) push('Código de barras/Linha', s.billing.codigoBarrasOuLinha);
    if (s?.generic?.key_values) {
      s.generic.key_values.forEach((kv: any) => push(kv.label, kv.value));
    }

    // fallback: se structured não veio, usa rawText simples
    if (!out.length) {
      const raw = get().extractsByDoc[docId]?.rawText;
      if (raw) out.push({ label: 'Texto OCR', value: raw.slice(0, 500) + (raw.length > 500 ? '…' : '') });
    }
    return out;
  },
  
  // Legacy compatibility methods
  addExtracts: (docId: string, extracts: ExtractResult[]) => {
    const timestamp = Date.now();
    set((state) => ({
      dataByDoc: {
        ...state.dataByDoc,
        [docId]: {
          docId,
          extracts: [...extracts], // Imutable array
          timestamp,
          lastUpdatedAt: timestamp
        }
      }
    }));
    
    // Also save to new system if structured data is available
    const structured = extracts.find(e => (e.meta as any)?.structured)?.meta;
    const rawText = extracts.map(e => e.text).join('\n\n');
    if (structured || rawText) {
      get().saveOCR(docId, { 
        rawText, 
        structured: (structured as any)?.structured 
      });
    }
    
    console.log('💾 Dados OCR salvos para documento:', docId, extracts.length, 'extracts');
  },
  
  getAllText: (docId: string): string => {
    const docData = get().dataByDoc[docId];
    if (!docData) return '';
    
    return docData.extracts.map(extract => extract.text).join('\n\n');
  },
  
  getCandidates: (docId: string, fieldType: FieldType): string[] => {
    const fullText = get().getAllText(docId);
    if (!fullText) return [];
    
    const candidates = extractCandidates(fullText, fieldType);
    console.log('🔍 Candidatos encontrados para', fieldType, ':', candidates.length);
    return candidates;
  },
  
  clearDocData: (docId: string) => {
    set((state) => {
      const newData = { ...state.dataByDoc };
      delete newData[docId];
      return { dataByDoc: newData };
    });
  },
  
  getAllImportantData: (docId: string): ImportantData => {
    const fullText = get().getAllText(docId);
    if (!fullText) return { names: [], documents: [], contacts: [], addresses: [], dates: [] };
    
    return {
      names: extractCandidates(fullText, 'name').slice(0, 5),
      documents: [
        ...extractCandidates(fullText, 'cpf').slice(0, 3),
        ...extractCandidates(fullText, 'rg').slice(0, 3)
      ].slice(0, 5),
      contacts: [
        ...extractCandidates(fullText, 'phone').slice(0, 3),
        ...extractCandidates(fullText, 'email').slice(0, 2)
      ].slice(0, 5),
      addresses: extractCandidates(fullText, 'address').slice(0, 3),
      dates: extractCandidates(fullText, 'date').slice(0, 3)
    };
  },
  
  parseStructuredFields: (text: string): string[] => {
    return parseStructuredFields(text);
  },
  
  // Legacy hasData now redirects to universal method
  // hasData: (docId: string): boolean => {
  //   return !!get().dataByDoc[docId];
  // },
  
  // Legacy getStructuredData now redirects to universal method
  // getStructuredData: (docId: string): any | null => {
  //   const docData = get().dataByDoc[docId];
  //   if (!docData || !docData.extracts.length) return null;
    
  //   // Check if any extract has meta.structured
  //   for (const extract of docData.extracts) {
  //     if ((extract.meta as any)?.structured) {
  //       return (extract.meta as any).structured;
  //     }
  //   }
    
  //   return null;
  // },
  
  hasStructuredData: (docId: string): boolean => {
    // Use universal method
    return get().hasData(docId);
  },

  generateDisplayDataFromRaw: (docId: string): any | null => {
    const fullText = get().getAllText(docId);
    if (!fullText) return null;

    console.log('🔧 Gerando displayData do texto bruto para:', docId);
    
    // Extract basic data using regex patterns
    const cpfRegex = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g;
    const rgRegex = /\b\d{1,2}\.?\d{3}\.?\d{3}-?[0-9Xx]\b/g;
    const phoneRegex = /\(?\d{2}\)?\s?\d{4,5}-?\d{4}/g;
    const dateRegex = /\b(0?[1-9]|[12]\d|3[01])[\/\-](0?[1-9]|1[0-2])[\/\-]\d{4}\b/g;
    const nameRegex = /\b[A-ZÁÉÍÓÚÂÊÎÔÛÀÈÌÒÙÇÑ][a-záéíóúâêîôûàèìòùçñ]+(?:\s+[A-ZÁÉÍÓÚÂÊÎÔÛÀÈÌÒÙÇÑ][a-záéíóúâêîôûàèìòùçñ]+){1,5}\b/g;

    const cpfs = fullText.match(cpfRegex) || [];
    const rgs = fullText.match(rgRegex) || [];
    const phones = fullText.match(phoneRegex) || [];
    const dates = fullText.match(dateRegex) || [];
    const names = (fullText.match(nameRegex) || []).filter(name => 
      name.length >= 5 && name.length <= 60 && name.split(' ').length >= 2
    );

    return {
      identification: {
        fullName: names[0] || '',
        cpf: cpfs.find(validateCPF) || cpfs[0] || '',
        rg: rgs[0] || '',
        cnh: ''
      },
      contacts: {
        phone: phones[0] || '',
        email: ''
      },
      dates: {
        birth: dates[0] || '',
        issue: dates[1] || '',
        expiry: dates[2] || ''
      },
      rawText: fullText
    };
  }
}));